const DAYS_OF_WEEK = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS = [
	'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
	'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
];

export const formatDateRussian = (dateStr: string): string => {
	const date = new Date(dateStr);
	if (isNaN(date.getTime())) return '';

	const dayOfWeek = DAYS_OF_WEEK[date.getDay()];
	const day = date.getDate();
	const month = MONTHS[date.getMonth()];

	return `${dayOfWeek}, ${day} ${month}`;
};

export const formatDateTimeRussian = (
	dateStr: string,
	startTime: string,
	endTime: string
): string => {
	const date = new Date(dateStr);
	if (isNaN(date.getTime())) return '';

	const dayOfWeek = DAYS_OF_WEEK[date.getDay()];
	const day = date.getDate();
	const month = MONTHS[date.getMonth()];

	return `${dayOfWeek}, ${day} ${month}, ${startTime}-${endTime}`;
};

export interface DateGroup<T> {
	dateLabel: string;
	dateKey: string;
	items: T[];
}

export function groupByDate<T extends { date: string }>(
	items: T[]
): DateGroup<T>[] {
	const groups: Record<string, { dateLabel: string; dateKey: string; items: T[] }> = {};

	for (const item of items) {
		if (!item.date) continue;

		const dateLabel = formatDateRussian(item.date);
		if (!dateLabel) continue;

		const dateKey = item.date;

		if (!groups[dateLabel]) {
			groups[dateLabel] = { dateLabel, dateKey, items: [] };
		}
		groups[dateLabel].items.push(item);
	}

	return Object.values(groups).sort((a, b) => {
		const dateA = new Date(a.dateKey).getTime();
		const dateB = new Date(b.dateKey).getTime();
		return dateA - dateB;
	});
}

/**
 * Фильтрует элементы за последние N месяцев (по умолчанию 6)
 */
export function filterLastMonths<T extends { date: string }>(
	items: T[],
	months: number = 6
): T[] {
	const cutoff = new Date();
	cutoff.setMonth(cutoff.getMonth() - months);

	return items.filter((item) => {
		const d = new Date(item.date);
		return !isNaN(d.getTime()) && d >= cutoff;
	});
}

export interface DateGroupOrYear<T> {
	type: 'date' | 'year';
	year?: number;
	dateLabel?: string;
	dateKey?: string;
	items?: T[];
}

/**
 * Группирует по дате в обратном хронологическом порядке с разделителями по годам.
 * Как в мобильном приложении: EnjoyingGamesScreen / CreateGamesScreen.
 */
export function groupByDateWithYears<T extends { date: string }>(
	items: T[]
): DateGroupOrYear<T>[] {
	const dateGroups = groupByDate(items);

	// Сортируем в обратном порядке (новые сначала)
	dateGroups.sort((a, b) => {
		const dateA = new Date(a.dateKey).getTime();
		const dateB = new Date(b.dateKey).getTime();
		return dateB - dateA;
	});

	const result: DateGroupOrYear<T>[] = [];
	let currentYear: number | null = null;

	for (const group of dateGroups) {
		const year = new Date(group.dateKey).getFullYear();
		if (year !== currentYear) {
			result.push({ type: 'year', year });
			currentYear = year;
		}
		result.push({
			type: 'date',
			dateLabel: group.dateLabel,
			dateKey: group.dateKey,
			items: group.items,
		});
	}

	return result;
}
